import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import type { MetricDto } from '@crm/shared/contracts';
import { formatNumber, formatPoints, formatSigned, formatSignedPercent } from '../lib/format.ts';
import { useLocale, useT } from '../lib/i18n.tsx';

/**
 * The change against the comparison period: absolute and relative for counts, percentage points
 * for rates. Direction is shown with an arrow and a word, never with a "good/bad" colour: more lost
 * deals may simply mean more leads (docs/DASHBOARD_SPECIFICATION.md §1, principle 3).
 */
export function DeltaBadge({
  metric,
  formatAmount,
}: {
  metric: MetricDto;
  /** Formats absolute amounts (the change and the previous value), e.g. money with its symbol. */
  formatAmount?: (value: number) => string;
}) {
  const t = useT();
  const locale = useLocale();
  if (metric.change === null || metric.previous === null) return null;

  const isRate = metric.unit === 'ratio';
  const direction = metric.change > 0 ? 'up' : metric.change < 0 ? 'down' : 'same';
  const Icon = direction === 'up' ? ArrowUp : direction === 'down' ? ArrowDown : Minus;
  const change = isRate
    ? `${formatPoints(metric.change, locale)} ${t.compare.pp}`
    : formatAmount
      ? `${metric.change > 0 ? '+' : metric.change < 0 ? '−' : ''}${formatAmount(Math.abs(metric.change))}`
      : formatSigned(metric.change, locale, metric.unit === 'containers_per_deal' ? 1 : 0);
  const relative =
    !isRate && metric.changeRatio !== null
      ? ` (${formatSignedPercent(metric.changeRatio, locale)})`
      : '';
  const previous = isRate
    ? formatNumber(metric.previous * 100, locale, 1) + '%'
    : formatAmount
      ? formatAmount(metric.previous)
      : formatNumber(metric.previous, locale, metric.unit === 'containers_per_deal' ? 1 : 0);

  return (
    <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
      <span className="inline-flex items-center gap-1 font-medium text-foreground">
        <Icon aria-hidden="true" className="size-3.5" />
        <span className="sr-only">{t.compare[direction]}: </span>
        {change}
        {relative}
      </span>
      <span className="text-xs">{t.compare.previous(previous)}</span>
    </p>
  );
}
